import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, BarChart3, Megaphone } from "lucide-react";
import { getConsent, saveConsent, OPEN_PREFS_EVENT } from "@/lib/consent";

const Toggle = ({ checked, onChange, disabled, testid }) => (
  <button type="button" role="switch" aria-checked={checked} disabled={disabled}
    onClick={() => !disabled && onChange(!checked)} data-testid={testid}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${checked ? "" : "bg-slate-300"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    style={checked ? { backgroundColor: "var(--sc-emerald)" } : {}}>
    <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
  </button>
);

const Row = ({ icon: Icon, title, desc, checked, onChange, disabled, testid }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-t border-slate-100">
    <div className="flex gap-3">
      <span className="w-9 h-9 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: "var(--sc-emerald)" }}><Icon size={17} /></span>
      <div>
        <p className="font-bold text-slate-900 text-sm">{title}{disabled && <span className="ml-2 text-xs font-semibold text-slate-400">(sempre attivi)</span>}</p>
        <p className="text-slate-600 text-sm mt-0.5">{desc}</p>
      </div>
    </div>
    <Toggle checked={checked} onChange={onChange} disabled={disabled} testid={testid} />
  </div>
);

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [prefs, setPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!getConsent()) setVisible(true);
    const open = () => {
      const c = getConsent();
      setAnalytics(c?.analytics || false);
      setMarketing(c?.marketing || false);
      setPrefs(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_PREFS_EVENT, open);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, open);
  }, []);

  if (!visible) return null;

  const done = () => { setVisible(false); setPrefs(false); };
  const acceptAll = () => { saveConsent({ analytics: true, marketing: true }); done(); };
  const rejectAll = () => { saveConsent({ analytics: false, marketing: false }); done(); };
  const savePrefs = () => { saveConsent({ analytics, marketing }); done(); };

  return (
    <div className="fixed inset-0 z-[2000] flex items-end sm:items-center sm:justify-center bg-slate-900/40 backdrop-blur-sm" data-testid="cookie-banner">
      <div className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl border border-slate-200 p-6 sm:m-4 animate-in slide-in-from-bottom duration-300">
        <div className="flex items-center gap-2">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: "var(--sc-emerald)" }}><Cookie size={20} /></span>
          <h2 className="text-lg font-extrabold text-slate-900" style={{ fontFamily: "Outfit" }}>Rispettiamo la tua privacy</h2>
        </div>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
          Usiamo cookie tecnici necessari al funzionamento del sito e, previo tuo consenso, cookie di analisi e di
          marketing. Puoi accettarli tutti, rifiutarli o scegliere le tue preferenze. Maggiori dettagli nella{" "}
          <Link to="/cookie" className="font-semibold text-emerald-800 underline">Cookie Policy</Link> e nella{" "}
          <Link to="/privacy" className="font-semibold text-emerald-800 underline">Privacy Policy</Link>.
        </p>

        {prefs && (
          <div className="mt-4" data-testid="cookie-prefs">
            <Row icon={ShieldCheck} title="Necessari" desc="Indispensabili per la navigazione e la sicurezza del sito." checked disabled />
            <Row icon={BarChart3} title="Analytics" desc="Ci aiutano a capire come viene usato il sito, in forma aggregata." checked={analytics} onChange={setAnalytics} testid="toggle-analytics" />
            <Row icon={Megaphone} title="Marketing" desc="Per misurare le campagne e mostrare contenuti pertinenti." checked={marketing} onChange={setMarketing} testid="toggle-marketing" />
          </div>
        )}

        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          {!prefs && (
            <button onClick={() => setPrefs(true)} className="order-3 sm:order-1 flex-1 h-11 rounded-full border border-slate-300 font-semibold text-slate-700 hover:bg-slate-50 text-sm" data-testid="cookie-customize">Personalizza</button>
          )}
          {prefs && (
            <button onClick={savePrefs} className="order-1 flex-1 h-11 rounded-full border border-slate-300 font-semibold text-slate-700 hover:bg-slate-50 text-sm" data-testid="cookie-save">Salva preferenze</button>
          )}
          <button onClick={rejectAll} className="order-2 flex-1 h-11 rounded-full border border-slate-300 font-semibold text-slate-700 hover:bg-slate-50 text-sm" data-testid="cookie-reject">Rifiuta non essenziali</button>
          <button onClick={acceptAll} className="order-1 sm:order-3 flex-1 h-11 rounded-full text-white font-bold text-sm" style={{ backgroundColor: "var(--sc-emerald)" }} data-testid="cookie-accept-all">Accetta tutti</button>
        </div>
      </div>
    </div>
  );
};
